'use client';

import { useParams } from 'next/navigation';
import { useProject } from '@/features/project/hooks/use-projects';

/**
 * Unobtrusive "used/limit" counter next to the album/estimate Generate button
 * on an UNLIMITED building. The server owns everything: it computes the meter
 * (Facade Pack per-building / Pro Plan per-period) and only sends it once
 * two-thirds of the cap is spent, so the client just renders `project
 * .usage_meters[kind]` verbatim when present — no counting, no threshold logic.
 *
 * Renders nothing when the meter is absent (token building, or still below the
 * reveal point). Self-contained like {@link PackUsageNotice}: reads the
 * building from the route and the meter from the cached project detail, so call
 * sites just drop `<GenerationUsageMeter kind="albums" />` under their button.
 */
export function GenerationUsageMeter({
  kind,
  className,
}: {
  kind: 'albums' | 'estimates' | 'hires';
  className?: string;
}) {
  const params = useParams<{ projectId?: string }>();
  const projectId =
    typeof params?.projectId === 'string' ? params.projectId : undefined;

  const { data: projectResponse } = useProject(projectId ?? '', {
    enabled: !!projectId,
  });
  const meter = projectResponse?.data?.attributes?.usage_meters?.[kind];

  if (!meter) return null;

  return (
    <span
      className={`text-card-meta text-muted-foreground tabular-nums text-center ${className ?? ''}`}
    >
      {meter.used}/{meter.limit}
    </span>
  );
}
